import { useEffect, useMemo, useState } from "react";
import { ALL_CUSTOMERS_SCOPE, conversationCustomerOptions, conversationMatchesCustomer, UNASSIGNED_CUSTOMER_SCOPE } from "../conversationSegmentation";
import { Icon } from "../icons";
import { VsmartContextCard } from "./VsmartContextCard";
import type { VsmartAction } from "../integrationApi";
import { countUnreadConversations } from "../conversationSync";
import type { Commitment, Conversation, ConversationStatus, MediaDownload, Message, Priority, Visit } from "../types";
import { MessageMediaPreview } from "./MessageMediaPreview";

const PRIORITY_LABEL: Record<Priority, string> = {
  urgent: "Urgente",
  high: "Alta",
  normal: "Normal",
  low: "Baja",
};

const STATUS_LABEL: Record<ConversationStatus, string> = {
  open: "Abierta",
  waiting: "Esperando respuesta",
  scheduled: "Visita agendada",
  resolved: "Resuelta",
};

const DELIVERY_LABEL = {
  accepted: "Enviado a Meta",
  sent: "Enviado",
  delivered: "Entregado",
  read: "Leído",
  failed: "Fallido",
} as const;

export function ConversationView({
  conversations,
  selectedId,
  onSelect,
  onToggleCommitment,
  onReviewCommitment,
  onRequestAnalysis,
  analyzingConversationId,
  onCreateCommitment,
  onUpdateOperations,
  onCreateVisit,
  onCreateReport,
  onMarkRead,
  onAddInternalNote,
  onToggleManagement,
  onSendWhatsApp,
  onResolveMedia,
  onOpenMedia,
  openingMediaId,
  onReviewMedia,
  reviewingMediaId,
  canReviewMedia,
  onTranscribeAudio,
  transcribingMessageId,
  canTranscribe,
  canControlManagement,
  canEditOperations,
  canReply,
  sendingConversationId,
  onQueryVsmart,
  canQueryVsmart,
  canReviewVsmart,
  canStartNewWhatsApp,
  onStartNewWhatsApp,
  segmentByCustomer,
  loadingDetail,
}: {
  conversations: Conversation[];
  selectedId: string;
  onSelect: (id: string) => void;
  onToggleCommitment: (conversationId: string, commitment: Commitment) => void;
  onReviewCommitment: (conversationId: string, commitment: Commitment, status: "confirmed" | "discarded") => void;
  onRequestAnalysis: (conversation: Conversation) => void;
  analyzingConversationId?: string;
  onCreateCommitment: (conversation: Conversation, input: Omit<Commitment, "id" | "conversationId" | "createdAt">) => Promise<void>;
  onUpdateOperations: (conversation: Conversation, changes: Partial<Pick<Conversation, "status" | "priority" | "assignedTo" | "nextAction">>) => Promise<void>;
  onCreateVisit: (conversation: Conversation, input: Pick<Visit, "scheduledAt" | "assignedTo" | "objective">) => Promise<void>;
  onCreateReport: (conversation: Conversation) => void;
  onMarkRead: (conversation: Conversation) => void;
  onAddInternalNote: (conversation: Conversation, note: string) => void;
  onToggleManagement: (conversation: Conversation, enabled: boolean) => void;
  onSendWhatsApp: (conversation: Conversation, body: string) => Promise<void>;
  onResolveMedia: (conversation: Conversation, message: Message) => Promise<MediaDownload>;
  onOpenMedia: (conversation: Conversation, message: Message) => Promise<void>;
  openingMediaId?: string;
  onReviewMedia: (conversation: Conversation, message: Message) => Promise<void>;
  reviewingMediaId?: string;
  canReviewMedia: boolean;
  onTranscribeAudio: (conversation: Conversation, message: Message) => Promise<void>;
  transcribingMessageId?: string;
  canTranscribe: boolean;
  canControlManagement: boolean;
  canEditOperations: boolean;
  canReply: boolean;
  sendingConversationId?: string;
  onQueryVsmart: (
  conversation: Conversation,
  action: VsmartAction,
  args?: Record<string, unknown>,
) => Promise<Record<string, unknown>>;
canQueryVsmart: boolean;
canReviewVsmart: boolean;
  canStartNewWhatsApp: boolean;
  onStartNewWhatsApp: () => void;
  segmentByCustomer: boolean;
  loadingDetail?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState<"all" | "unread" | "urgent">("all");
  const [customerScope, setCustomerScope] = useState("");
  const [note, setNote] = useState("");
  const [reply, setReply] = useState("");
  const [composerMode, setComposerMode] = useState<"reply" | "note">("reply");
  const [mobilePane, setMobilePane] = useState<"list" | "chat" | "context">("list");
  const [editingOperations, setEditingOperations] = useState(false);
  const [operationDraft, setOperationDraft] = useState<{
    status: ConversationStatus;
    priority: Priority;
    assignedTo: string;
    nextAction: string;
  }>({ status: "open", priority: "normal", assignedTo: "", nextAction: "" });
  const [savingOperations, setSavingOperations] = useState(false);
  const [addingCommitment, setAddingCommitment] = useState(false);
  const [commitmentDraft, setCommitmentDraft] = useState({ description: "", responsible: "", dueAt: "" });
  const [savingCommitment, setSavingCommitment] = useState(false);
  const [visitFormOpen, setVisitFormOpen] = useState(false);
  const [visitDraft, setVisitDraft] = useState({ scheduledAt: "", assignedTo: "", objective: "" });
  const [savingVisit, setSavingVisit] = useState(false);

  const customerOptions = useMemo(
    () => segmentByCustomer ? conversationCustomerOptions(conversations) : [],
    [conversations, segmentByCustomer],
  );
  useEffect(() => {
    if (!segmentByCustomer) {
      setCustomerScope("");
      return;
    }
    if (customerScope === ALL_CUSTOMERS_SCOPE || (customerScope && customerOptions.some((option) => option.id === customerScope))) return;
    setCustomerScope(customerOptions.find((option) => option.id !== UNASSIGNED_CUSTOMER_SCOPE)?.id ?? customerOptions[0]?.id ?? "");
  }, [customerOptions, customerScope, segmentByCustomer]);
  const customerScoped = useMemo(
    () => conversations.filter((item) => !segmentByCustomer || conversationMatchesCustomer(item, customerScope)),
    [conversations, customerScope, segmentByCustomer],
  );
  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase("es-CL");
    return customerScoped.filter((item) => {
      const matchesScope = scope === "all" || (scope === "unread" && item.unreadCount > 0) || (scope === "urgent" && item.priority === "urgent");
      const matchesSearch = !term || [item.customer, item.site, item.contactName, item.summary, ...item.tags].join(" ").toLocaleLowerCase("es-CL").includes(term);
      return matchesScope && matchesSearch;
    });
  }, [customerScoped, scope, search]);

  const selected = filtered.find((item) => item.id === selectedId) ?? filtered[0];
  const unread = countUnreadConversations(customerScoped);
  const urgent = customerScoped.filter((item) => item.priority === "urgent").length;
  const lastInboundMs = selected ? Date.parse(selected.lastInboundAt ?? selected.lastMessageAt) : Number.NaN;
  const replyWindowOpen = Number.isFinite(lastInboundMs) && Date.now() - lastInboundMs <= 24 * 60 * 60 * 1_000;
  const sendingReply = Boolean(selected && sendingConversationId === selected.id);
  const analysisRunning = Boolean(selected && (analyzingConversationId === selected.id || ["queued", "analyzing"].includes(selected.analysisStatus ?? "")));
  const analysisLabel = selected?.analysisStatus === "ready"
    ? "Análisis actualizado"
    : selected?.analysisStatus === "error"
      ? "Análisis con error"
      : analysisRunning
        ? "Analizando"
        : "Análisis pendiente";
  const hasWhatsApp = Boolean(selected?.phoneNumberId && selected.source !== "retell_call");
  const isOperationalGroup = Boolean(selected?.id.includes("#group#"));

  function choose(id: string) {
    const conversation = conversations.find((item) => item.id === id);
    if (conversation?.unreadCount) onMarkRead(conversation);
    onSelect(id);
    setEditingOperations(false);
    setAddingCommitment(false);
    setVisitFormOpen(false);
    setMobilePane("chat");
  }

  function submitNote() {
    const value = note.trim();
    if (!selected || !value) return;
    onAddInternalNote(selected, value);
    setNote("");
  }

  async function submitReply() {
    const value = reply.trim();
    if (!selected || !value || sendingReply) return;
    try {
      await onSendWhatsApp(selected, value);
      setReply("");
    } catch {
      // App keeps the draft and shows the actionable error.
    }
  }

  function beginOperationsEdit() {
    if (!selected) return;
    setOperationDraft({
      status: selected.status,
      priority: selected.priority,
      assignedTo: selected.assignedTo === "Sin asignar" ? "" : selected.assignedTo,
      nextAction: selected.nextAction ?? "",
    });
    setEditingOperations(true);
  }

  async function submitOperations() {
    if (!selected || savingOperations) return;
    setSavingOperations(true);
    try {
      await onUpdateOperations(selected, {
        status: operationDraft.status,
        priority: operationDraft.priority,
        assignedTo: operationDraft.assignedTo.trim() || "Sin asignar",
        nextAction: operationDraft.nextAction.trim(),
      });
      setEditingOperations(false);
    } catch {
      // App keeps the form open and shows the actionable error.
    } finally {
      setSavingOperations(false);
    }
  }

  function beginCommitment() {
    if (!selected) return;
    setCommitmentDraft({
      description: "",
      responsible: selected.assignedTo === "Sin asignar" ? "" : selected.assignedTo,
      dueAt: "",
    });
    setAddingCommitment(true);
  }

  async function submitCommitment() {
    if (!selected || savingCommitment || !commitmentDraft.description.trim() || !commitmentDraft.responsible.trim()) return;
    setSavingCommitment(true);
    try {
      await onCreateCommitment(selected, {
        description: commitmentDraft.description.trim(),
        responsible: commitmentDraft.responsible.trim(),
        dueAt: commitmentDraft.dueAt,
        status: "pending",
        sourceMessageId: "",
      });
      setAddingCommitment(false);
      setCommitmentDraft({ description: "", responsible: "", dueAt: "" });
    } catch {
      // App keeps the draft and shows the actionable error.
    } finally {
      setSavingCommitment(false);
    }
  }

  function beginVisit() {
    if (!selected) return;
    setVisitDraft({
      scheduledAt: "",
      assignedTo: selected.assignedTo === "Sin asignar" ? "" : selected.assignedTo,
      objective: selected.commitments.find((item) => item.status === "pending")?.description ?? selected.summary,
    });
    setVisitFormOpen(true);
    setMobilePane("context");
  }

  async function submitVisit() {
    if (!selected || savingVisit || !visitDraft.scheduledAt || !visitDraft.assignedTo.trim() || !visitDraft.objective.trim()) return;
    setSavingVisit(true);
    try {
      await onCreateVisit(selected, {
        scheduledAt: new Date(visitDraft.scheduledAt).toISOString(),
        assignedTo: visitDraft.assignedTo.trim(),
        objective: visitDraft.objective.trim(),
      });
      setVisitFormOpen(false);
    } catch {
      // App keeps the draft and shows the actionable error.
    } finally {
      setSavingVisit(false);
    }
  }

  return (
    <div className="conversation-page">
      <div className="conversation-mobile-tabs">
        <button className={mobilePane === "list" ? "active" : ""} onClick={() => setMobilePane("list")} type="button"><Icon name="chat" size={16} /> Bandeja</button>
        <button className={mobilePane === "chat" ? "active" : ""} onClick={() => setMobilePane("chat")} type="button"><Icon name="whatsapp" size={16} /> Conversación</button>
        <button className={mobilePane === "context" ? "active" : ""} onClick={() => setMobilePane("context")} type="button"><Icon name="sparkles" size={16} /> Seguimiento</button>
      </div>

      <div className={`conversation-workspace pane-${mobilePane}`}>
        <aside className="inbox-pane">
          <div className="pane-header">
            <div><p className="section-kicker">BANDEJA</p><h1>Conversaciones</h1></div>
            <div className="inbox-heading-actions">{canStartNewWhatsApp ? <button className="button primary compact" onClick={onStartNewWhatsApp} type="button"><Icon name="whatsapp" size={15} /> Nuevo WhatsApp</button> : null}<span className="count-badge">{filtered.length}</span></div>
          </div>
          {segmentByCustomer ? <label className="inbox-customer-filter"><span>Cliente / condominio</span><select value={customerScope} onChange={(event) => setCustomerScope(event.target.value)}><option value={ALL_CUSTOMERS_SCOPE}>Visión general: todos ({conversations.length})</option>{customerOptions.map((option) => <option key={option.id} value={option.id}>{option.label} ({option.count})</option>)}</select></label> : null}
          <label className="search-field"><Icon name="search" size={17} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar cliente, instalación o tema" /></label>
          <div className="filter-pills">
            <button className={scope === "all" ? "active" : ""} onClick={() => setScope("all")} type="button">Todas</button>
            <button className={scope === "unread" ? "active" : ""} onClick={() => setScope("unread")} type="button">Sin leer <span>{unread}</span></button>
            <button className={scope === "urgent" ? "active" : ""} onClick={() => setScope("urgent")} type="button">Urgentes <span>{urgent}</span></button>
          </div>
          <div className="inbox-list">
            {filtered.map((item) => (
              <button className={`inbox-item ${selected?.id === item.id ? "selected" : ""}`} key={item.id} onClick={() => choose(item.id)} type="button">
                <span className="customer-avatar">{initials(item.customer)}</span>
                <span className="inbox-item-content">
                  <span className="inbox-line"><strong>{item.customer}</strong><time>{shortTime(item.lastMessageAt)}</time></span>
                  <span className="inbox-site">{item.site} · {item.contactName}</span>
                  <span className="inbox-preview">{item.lastMessage}</span>
                  <span className="inbox-meta"><i className={`priority-label ${item.priority}`}>{PRIORITY_LABEL[item.priority]}</i>{item.phoneNumberId ? <em className={item.managedFromApp ? "managed" : "observe"}>{item.managedFromApp ? "Gestionada" : "Observación"}</em> : null}<span>{item.assignedTo}</span>{item.unreadCount ? <b>{item.unreadCount}</b> : null}</span>
                </span>
              </button>
            ))}
            {!filtered.length ? <div className="friendly-empty"><span><Icon name="search" size={23} /></span><strong>No encontramos conversaciones</strong><p>{canStartNewWhatsApp ? "Inicia un WhatsApp con un residente autorizado o espera su primer mensaje." : "Prueba con otro filtro o importa un nuevo chat."}</p></div> : null}
          </div>
        </aside>

        <section className="chat-pane">
          {!selected ? (
            <div className="friendly-empty full"><span><Icon name="chat" size={26} /></span><strong>Selecciona una conversación</strong><p>El chat y su seguimiento aparecerán aquí.</p></div>
          ) : (
            <>
              <header className="chat-pane-header">
                <div className="chat-person"><span className="customer-avatar large">{initials(selected.customer)}</span><div><p className="section-kicker">{selected.company}</p><h2>{selected.customer} <span>· {selected.site}</span></h2><small><i className="presence-dot" /> {selected.contactName} · {selected.phone}</small></div></div>
                <div className="chat-toolbar"><span className={`status-label ${selected.status}`}>{STATUS_LABEL[selected.status]}</span>{hasWhatsApp ? <button className={`management-mode ${selected.managedFromApp ? "managed" : "observe"}`} disabled={!canControlManagement} onClick={() => onToggleManagement(selected, !selected.managedFromApp)} title={canControlManagement ? "Cambiar cómo se maneja esta conversación" : "Tu rol no puede cambiar este ajuste"} type="button"><Icon name={selected.managedFromApp ? "check" : "clock"} size={14} />{selected.managedFromApp ? "Gestionada aquí" : "Sólo observar"}</button> : <span className="management-mode historical">Histórico</span>}{selected.unreadCount ? <button className="button soft compact" onClick={() => onMarkRead(selected)} type="button"><Icon name="check" size={15} /> Marcar leída</button> : null}</div>
              </header>
              <div className="chat-context-strip"><span><Icon name={selected.source === "retell_call" ? "clock" : "whatsapp"} size={15} /> {selected.source === "retell_call" ? "Llamadas Retell" : selected.source === "whatsapp_export" ? "Chat importado" : "WhatsApp Business"}</span><span><Icon name="team" size={15} /> {selected.assignedTo}</span><span><Icon name="clock" size={15} /> {selected.nextAction ?? "Revisar conversación"}</span>{hasWhatsApp ? <span className={replyWindowOpen ? "window-open" : "window-closed"}>{replyWindowOpen ? "Ventana de respuesta abierta" : "Requiere plantilla"}</span> : null}</div>
              <div className="chat-scroll">
                {loadingDetail ? <div className="loading-line"><span /> Cargando evidencia…</div> : null}
                <div className="date-separator"><span>CONVERSACIÓN</span></div>
                {selected.messages.map((message) => (
                  <div className={`message-line ${message.direction}`} id={`message-${message.id}`} key={message.id}>
                    <span className="message-avatar">{message.direction === "outbound" ? "EQ" : initials(message.sender)}</span>
                    <article className={`message-bubble ${message.type !== "text" ? "media" : ""} ${message.type === "call" ? "call-evidence-bubble" : ""}`}>
                      <header><strong>{message.sender}</strong><span>{message.type === "call" ? "Llamada Retell" : message.type === "system" ? "Sistema" : message.type === "text" ? "WhatsApp" : message.type}</span></header>
                      {message.type === "call" ? (
                        <div className="call-evidence">
                          <div className="call-evidence-title"><span className="connection-icon call"><Icon name="clock" size={18} /></span><div><strong>{message.direction === "inbound" ? "Llamada recibida" : "Llamada saliente"}</strong><small>{callDuration(message.durationSeconds)}{message.sentiment ? " · " + sentimentLabel(message.sentiment) : ""}</small></div>{typeof message.successful === "boolean" ? <span className={message.successful ? "state-chip active" : "state-chip error"}>{message.successful ? "Resuelta" : "Revisar"}</span> : null}</div>
                          <p className="call-summary">{message.body}</p>
                          {message.recordingUrl ? <a className="call-recording-link" href={message.recordingUrl} rel="noreferrer" target="_blank"><Icon name="external" size={14} /> Escuchar grabación</a> : null}
                          {message.transcript ? <details className="call-transcript"><summary>Ver transcript completo</summary><pre>{message.transcript}</pre></details> : <p className="call-transcript-pending">Transcript pendiente del análisis de Retell.</p>}
                        </div>
                      ) : (
                        <>
                          {message.type !== "text" && message.type !== "system" ? <MessageMediaPreview message={message} onResolve={() => onResolveMedia(selected, message)} /> : null}
                          {message.mediaStatus === "stored" ? <button className="call-recording-link" disabled={openingMediaId === message.id} onClick={() => void onOpenMedia(selected, message)} type="button"><Icon name="external" size={14} /> {openingMediaId === message.id ? "Preparando acceso…" : `Abrir ${mediaLabel(message.type)}`}</button> : null}
                          {["image", "document", "video"].includes(message.type) && message.mediaReviewSummary ? <details className="call-transcript"><summary>{message.mediaReviewScope === "video_audio" ? "Ver revisión del audio del video" : "Ver revisión de la evidencia"}</summary><pre>{message.mediaReviewSummary}</pre>{message.mediaReviewScope === "video_audio" ? <small>La revisión del video se limita a su audio; las imágenes deben verificarse manualmente.</small> : null}</details> : null}
                          {["image", "document", "video"].includes(message.type) && ["queued", "in_progress"].includes(message.mediaReviewStatus ?? "") ? <p className="call-transcript-pending">Revisión en proceso con OpenAI. Puedes seguir trabajando mientras se analiza la evidencia.</p> : null}
                          {["image", "document", "video"].includes(message.type) && message.mediaReviewStatus === "failed" ? <p className="call-transcript-pending">No fue posible revisar la evidencia. {message.mediaReviewError ? `Detalle: ${message.mediaReviewError}` : "Puedes volver a intentarlo."}</p> : null}
                          {["image", "document", "video"].includes(message.type) && message.mediaStatus === "stored" && !message.mediaReviewSummary && !["queued", "in_progress"].includes(message.mediaReviewStatus ?? "") && canReviewMedia && isOperationalGroup ? <button className="call-recording-link" disabled={reviewingMediaId === message.id} onClick={() => void onReviewMedia(selected, message)} type="button"><Icon name="sparkles" size={14} /> {reviewingMediaId === message.id ? "Iniciando revisión…" : message.mediaReviewStatus === "failed" ? "Reintentar revisión" : message.type === "video" ? "Revisar audio del video" : "Revisar con IA"}</button> : null}
                          {message.type === "audio" && message.transcript ? <details className="call-transcript"><summary>Ver transcripción del audio</summary><pre>{message.transcript}</pre></details> : null}
                          {message.type === "audio" && ["queued", "in_progress"].includes(message.transcriptionStatus ?? "") ? <p className="call-transcript-pending">Transcripción en proceso con OpenAI. Puedes seguir trabajando mientras se analiza el audio.</p> : null}
                          {message.type === "audio" && message.transcriptionStatus === "failed" ? <p className="call-transcript-pending">No fue posible transcribir el audio. {message.transcriptionError ? `Detalle: ${message.transcriptionError}` : "Puedes volver a intentarlo."}</p> : null}
                          {message.type === "audio" && message.mediaStatus === "stored" && !message.transcript && !["queued", "in_progress"].includes(message.transcriptionStatus ?? "") && canTranscribe ? <button className="call-recording-link" disabled={transcribingMessageId === message.id} onClick={() => void onTranscribeAudio(selected, message)} type="button"><Icon name="sparkles" size={14} /> {transcribingMessageId === message.id ? "Iniciando transcripción…" : message.transcriptionStatus === "failed" ? "Reintentar transcripción" : "Transcribir audio"}</button> : null}
                          {message.mediaStatus === "failed" ? <p className="call-transcript-pending">El mensaje llegó, pero su archivo no pudo almacenarse. {message.mediaError ? `Detalle: ${message.mediaError}` : ""}</p> : null}
                          {message.mediaStatus === "skipped" && message.mediaError !== "MEDIA_CAPTURE_DISABLED" ? <p className="call-transcript-pending">Archivo omitido por política: {message.mediaError ?? "sin detalle"}.</p> : null}
                          <p>{message.body}</p>
                        </>
                      )}
                      <footer><time>{shortTime(message.sentAt)}</time>{message.direction === "outbound" && message.type !== "system" && message.type !== "call" ? <span className={`delivered ${message.deliveryStatus ?? "accepted"}`}>{DELIVERY_LABEL[message.deliveryStatus ?? "accepted"]}</span> : null}</footer>
                    </article>
                  </div>
                ))}
                {!selected.messages.length ? <div className="friendly-empty"><span><Icon name="chat" size={23} /></span><strong>Sin mensajes cargados</strong><p>Cuando llegue un mensaje de WhatsApp aparecerá aquí como evidencia verificable.</p></div> : null}
              </div>
              <div className={`internal-note-box conversation-composer ${composerMode}`}>
                <div className="composer-tabs" role="tablist" aria-label="Tipo de mensaje">
                  <button className={composerMode === "reply" ? "active" : ""} onClick={() => setComposerMode("reply")} role="tab" type="button"><Icon name="whatsapp" size={15} /> Responder por WhatsApp</button>
                  <button className={composerMode === "note" ? "active" : ""} onClick={() => setComposerMode("note")} role="tab" type="button"><Icon name="document" size={15} /> Nota interna</button>
                </div>
                {composerMode === "note" ? (
                  <>
                    <div className="note-label"><span>Nota para el equipo</span><small>No se envía por WhatsApp</small></div>
                    <textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="Registra una observación para el equipo…" />
                    <div><span className="meta-note">Quedará guardada con autor y fecha.</span><button className="button primary compact" disabled={!note.trim()} onClick={submitNote} type="button">Guardar nota <Icon name="arrow" size={15} /></button></div>
                  </>
                ) : !hasWhatsApp ? (
                  <div className="composer-gate"><strong>{selected.source === "retell_call" ? "Conversación de llamadas" : "Conversación histórica"}</strong><span>{selected.source === "retell_call" ? "Esta vista conserva la llamada y su transcript. Vincúlala a un número de WhatsApp en Canales si también quieres responder al cliente." : "Los chats importados se conservan como evidencia y no envían mensajes."}</span></div>
                ) : !selected.managedFromApp ? (
                  <div className="composer-gate"><strong>Esta conversación está en sólo observación</strong><span>Habilítala únicamente si el equipo asumirá su atención desde Conversa Smart.</span>{canControlManagement ? <button className="button primary compact" onClick={() => onToggleManagement(selected, true)} type="button">Habilitar gestión <Icon name="arrow" size={15} /></button> : null}</div>
                ) : !canReply ? (
                  <div className="composer-gate"><strong>Acceso de sólo lectura</strong><span>Tu rol puede revisar la evidencia, pero no responder al cliente.</span></div>
                ) : !replyWindowOpen ? (
                  <div className="composer-gate warning"><strong>La ventana de respuesta está cerrada</strong><span>Meta exige una plantilla aprobada para volver a iniciar la conversación. Las plantillas se habilitarán en la siguiente etapa.</span></div>
                ) : (
                  <>
                    <div className="note-label"><span><Icon name="whatsapp" size={15} /> Respuesta al cliente</span><small>Se enviará desde el número que recibió el chat</small></div>
                    <textarea maxLength={4096} value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Escribe una respuesta clara para el cliente…" />
                    <div><span className="meta-note">{reply.length}/4096 · queda registrada como evidencia</span><button className="button primary compact" disabled={!reply.trim() || sendingReply} onClick={() => void submitReply()} type="button">{sendingReply ? "Enviando…" : "Enviar por WhatsApp"} <Icon name="arrow" size={15} /></button></div>
                  </>
                )}
              </div>
            </>
          )}
        </section>

        <aside className="context-pane">
          {selected ? (
            <>
              <div className="pane-header context-heading"><div><p className="section-kicker">CONTEXTO</p><h2>Qué sigue</h2></div><div className="analysis-heading-actions"><span className={`ai-chip ${selected.analysisStatus ?? "pending"}`} title={selected.analysisError}><Icon name="sparkles" size={14} /> {analysisLabel}</span>{canEditOperations ? <button className="context-inline-action" disabled={analysisRunning || !selected.messages.length} onClick={() => onRequestAnalysis(selected)} type="button">{analysisRunning ? "Procesando…" : "Analizar ahora"}</button> : null}</div></div>
              <div className="context-scroll">
                <section className="context-card summary-card-context"><div className="context-card-title"><span><Icon name="sparkles" size={17} /> Resumen</span><small>{selected.analysisConfidence != null ? `${Math.round(selected.analysisConfidence * 100)}% confianza · revisable` : "Revisable"}</small></div><p>{selected.summary}</p>{selected.analysisSuggestedStatus && selected.analysisSuggestedStatus !== selected.status ? <p className="analysis-suggestion">Estado sugerido: <strong>{STATUS_LABEL[selected.analysisSuggestedStatus]}</strong></p> : null}<div className="tag-list">{selected.tags.map((tag) => <span key={tag}>{tag}</span>)}</div></section>
                <VsmartContextCard conversation={selected} enabled={canQueryVsmart} canReview={canReviewVsmart} onQuery={onQueryVsmart} />
                <section className="context-card">
                  <div className="context-card-title">
                    <span><Icon name="check" size={17} /> Compromisos</span>
                    <div className="context-title-actions"><small>{selected.commitments.filter((item) => item.status === "pending").length} pendientes</small>{canEditOperations && !addingCommitment ? <button className="context-inline-action" onClick={beginCommitment} type="button">Agregar</button> : null}</div>
                  </div>
                  <div className="commitment-list">{selected.commitments.map((commitment) => <CommitmentRow canReview={canEditOperations} commitment={commitment} key={commitment.id} onReview={(status) => onReviewCommitment(selected.id, commitment, status)} onToggle={() => onToggleCommitment(selected.id, commitment)} />)}{!selected.commitments.length && !addingCommitment ? <p className="empty-copy">Aún no hay compromisos. Agrega uno al revisar la conversación.</p> : null}</div>
                  {addingCommitment ? <div className="context-edit-form commitment-form">
                    <label>Compromiso<input autoFocus value={commitmentDraft.description} onChange={(event) => setCommitmentDraft((draft) => ({ ...draft, description: event.target.value }))} placeholder="Ej. Enviar propuesta actualizada" /></label>
                    <label>Responsable<input value={commitmentDraft.responsible} onChange={(event) => setCommitmentDraft((draft) => ({ ...draft, responsible: event.target.value }))} placeholder="Nombre o equipo" /></label>
                    <label>Fecha límite<input type="date" value={commitmentDraft.dueAt} onChange={(event) => setCommitmentDraft((draft) => ({ ...draft, dueAt: event.target.value }))} /></label>
                    <div className="context-form-actions"><button className="button soft compact" onClick={() => setAddingCommitment(false)} type="button">Cancelar</button><button className="button primary compact" disabled={!commitmentDraft.description.trim() || !commitmentDraft.responsible.trim() || savingCommitment} onClick={() => void submitCommitment()} type="button">{savingCommitment ? "Guardando…" : "Crear compromiso"}</button></div>
                  </div> : null}
                </section>
                <section className="context-card">
                  <div className="context-card-title"><span><Icon name="clock" size={17} /> Estado operativo</span>{canEditOperations && !editingOperations ? <button className="context-inline-action" onClick={beginOperationsEdit} type="button">Editar</button> : null}</div>
                  {editingOperations ? <div className="context-edit-form operations-form">
                    <label>Estado<select value={operationDraft.status} onChange={(event) => setOperationDraft((draft) => ({ ...draft, status: event.target.value as ConversationStatus }))}><option value="open">Abierta</option><option value="waiting">Esperando respuesta</option><option value="scheduled">Visita agendada</option><option value="resolved">Resuelta</option></select></label>
                    <label>Prioridad<select value={operationDraft.priority} onChange={(event) => setOperationDraft((draft) => ({ ...draft, priority: event.target.value as Priority }))}><option value="urgent">Urgente</option><option value="high">Alta</option><option value="normal">Normal</option><option value="low">Baja</option></select></label>
                    <label>Responsable<input value={operationDraft.assignedTo} onChange={(event) => setOperationDraft((draft) => ({ ...draft, assignedTo: event.target.value }))} placeholder="Nombre o equipo" /></label>
                    <label>Próxima acción<input value={operationDraft.nextAction} onChange={(event) => setOperationDraft((draft) => ({ ...draft, nextAction: event.target.value }))} placeholder="Qué debe ocurrir ahora" /></label>
                    <div className="context-form-actions"><button className="button soft compact" onClick={() => setEditingOperations(false)} type="button">Cancelar</button><button className="button primary compact" disabled={savingOperations} onClick={() => void submitOperations()} type="button">{savingOperations ? "Guardando…" : "Guardar"}</button></div>
                  </div> : <dl className="detail-list"><div><dt>Estado</dt><dd>{STATUS_LABEL[selected.status]}</dd></div><div><dt>Prioridad</dt><dd className={`priority-text ${selected.priority}`}>{PRIORITY_LABEL[selected.priority]}</dd></div><div><dt>Responsable</dt><dd>{selected.assignedTo}</dd></div><div><dt>Próxima acción</dt><dd>{selected.nextAction ?? selected.commitments[0]?.description ?? "Por definir"}</dd></div></dl>}
                </section>
                {visitFormOpen ? <section className="context-card visit-scheduler">
                  <div className="context-card-title"><span><Icon name="visit" size={17} /> Agendar visita</span><button className="context-inline-action" onClick={() => setVisitFormOpen(false)} type="button">Cerrar</button></div>
                  <div className="context-edit-form">
                    <label>Fecha y hora<input autoFocus type="datetime-local" value={visitDraft.scheduledAt} onChange={(event) => setVisitDraft((draft) => ({ ...draft, scheduledAt: event.target.value }))} /></label>
                    <label>Responsable<input value={visitDraft.assignedTo} onChange={(event) => setVisitDraft((draft) => ({ ...draft, assignedTo: event.target.value }))} placeholder="Técnico o equipo" /></label>
                    <label>Objetivo<textarea value={visitDraft.objective} onChange={(event) => setVisitDraft((draft) => ({ ...draft, objective: event.target.value }))} placeholder="Qué debe resolverse en terreno" /></label>
                    <div className="context-form-actions"><button className="button soft compact" onClick={() => setVisitFormOpen(false)} type="button">Cancelar</button><button className="button primary compact" disabled={!visitDraft.scheduledAt || !visitDraft.assignedTo.trim() || !visitDraft.objective.trim() || savingVisit} onClick={() => void submitVisit()} type="button">{savingVisit ? "Agendando…" : "Agendar visita"}</button></div>
                  </div>
                </section> : null}
                <div className="context-actions"><button className="button secondary wide" onClick={beginVisit} type="button"><Icon name="visit" size={17} /> {visitFormOpen ? "Editar visita" : "Crear visita"}</button><button className="button primary wide" onClick={() => onCreateReport(selected)} type="button"><Icon name="document" size={17} /> Generar reporte</button></div>
              </div>
            </>
          ) : null}
        </aside>
      </div>
    </div>
  );
}

function CommitmentRow({
  commitment,
  onToggle,
  onReview,
  canReview,
}: {
  commitment: Commitment;
  onToggle: () => void;
  onReview: (status: "confirmed" | "discarded") => void;
  canReview: boolean;
}) {
  function focusSource() {
    const target = document.getElementById(`message-${commitment.sourceMessageId}`);
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
    target?.classList.add("source-highlight");
    window.setTimeout(() => target?.classList.remove("source-highlight"), 1_600);
  }
  const suggested = commitment.source === "ai" && commitment.reviewStatus === "suggested";
  return <article className={`commitment-row ${commitment.status === "done" ? "done" : ""} ${suggested ? "ai-suggested" : ""}`}><button className="commitment-check" disabled={suggested} onClick={onToggle} title={suggested ? "Confirma la sugerencia antes de completarla" : undefined} type="button" aria-label={commitment.status === "done" ? "Reabrir compromiso" : "Completar compromiso"}>{commitment.status === "done" ? "✓" : ""}</button><div><div className="commitment-title-line"><strong>{commitment.description}</strong>{commitment.source === "ai" ? <span className="ai-origin-chip">IA{commitment.confidence != null ? ` ${Math.round(commitment.confidence * 100)}%` : ""}</span> : null}</div><span>{commitment.responsible}{commitment.dueAt ? ` · ${commitment.dueAt}` : ""}</span><div className="commitment-evidence-actions">{commitment.sourceMessageId ? <button className="source-link" onClick={focusSource} type="button">Ver mensaje origen <Icon name="external" size={12} /></button> : null}{suggested && canReview ? <><button className="source-link confirm" onClick={() => onReview("confirmed")} type="button">Confirmar</button><button className="source-link discard" onClick={() => onReview("discarded")} type="button">Descartar</button></> : null}</div></div></article>;
}

function initials(value: string) {
  return value.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "CS";
}

function shortTime(value: string) {
  if (!value) return "";
  if (/^\d{1,2}:\d{2}$/.test(value)) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value.slice(0, 12) : new Intl.DateTimeFormat("es-CL", { hour: "2-digit", minute: "2-digit" }).format(date);
}


function mediaLabel(type: Message["type"]) {
  if (type === "audio") return "audio";
  if (type === "image") return "imagen";
  if (type === "video") return "video";
  if (type === "document") return "documento";
  return "archivo";
}

function callDuration(seconds?: number) {
  if (seconds == null || !Number.isFinite(seconds)) return "Duración no disponible";
  if (seconds < 60) return seconds + " s";
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder ? minutes + " min " + remainder + " s" : minutes + " min";
}

function sentimentLabel(value: string) {
  const normalized = value.toLowerCase();
  if (normalized.includes("positive")) return "Sentimiento positivo";
  if (normalized.includes("negative")) return "Sentimiento negativo";
  if (normalized.includes("neutral")) return "Sentimiento neutral";
  return value;
}
